import { PushErrorNotification, PushProcessingNotification } from "@repo/helpers";
import { Grid } from "@repo/ui";
import { GridBaseProps, GridList, GridPagination, GridSaveState } from "@repo/ui/grid/types";
import { UseQueryResult } from "@tanstack/react-query";
import { useCallback } from "react";

export interface TLPGridApi {
	loadGridSettings: () => Promise<string | null>;
	saveGridSettings: (settings: string) => Promise<any>;
}

export interface TLPGridProps<T, TFilters extends GridPagination> extends Omit<
	GridBaseProps<T>,
	| "values"
	| "totalItems"
	| "currentPage"
	| "pageSize"
	| "loading"
	| "getData"
	| "pageChanged"
	| "setItemsPerPage"
	| "refresh"
> {
	filters: TFilters;
	setFilters: (filters: TFilters) => void;
	query: UseQueryResult<GridList<T>>;
	gridApi?: TLPGridApi;
	/** The grid's list fetcher; export re-sends the current filters with `export: true` and the server returns every matching row. */
	exportFunc?: (filters: TFilters & { export: true }) => Promise<GridList<T>>;
	isEnriching?: boolean;
	refresh?: () => void;
}

const DEFAULT_PAGE_SIZES = [25, 50, 100];

/** Cloud grids enable standard controls by default; pass false to disable individual features. */
export function TLPGrid<T, TFilters extends GridPagination>(props: TLPGridProps<T, TFilters>) {
	const {
		filters,
		setFilters,
		query,
		gridApi,
		exportFunc,
		exportable = true,
		gridNameForExport = props.title,
		sortable = true,
		resizeable = true,
		reorderable = true,
		changeVisibility = true,
		page = true,
		canSetItemsPerPage = true,
		pageSizes = DEFAULT_PAGE_SIZES,
		isEnriching,
		maxHeight = "max(80vh, 26rem)",
		...rest
	} = props;

	const loadGridState = useCallback(
		async (callback: (settings: GridSaveState | null) => void) => {
			if (!gridApi) return;
			try {
				const result = await gridApi.loadGridSettings();
				const parsed = result ? JSON.parse(result) : null;
				callback(parsed);
			} catch (e) {
				console.error("Failed to load grid settings", e);
			}
		},
		[gridApi],
	);

	const saveGridState = useCallback(
		async (config: string) => {
			if (!gridApi) return;
			const notify = PushProcessingNotification("Updating grid settings");
			try {
				if (!config) {
					await gridApi.saveGridSettings("");
					notify.success("Cleared grid settings");
					window.location.reload();
				} else {
					await gridApi.saveGridSettings(config);
					notify.success("Saved grid settings");
				}
			} catch (e) {
				notify.error("Failed to save grid settings");
				throw e;
			}
		},
		[gridApi],
	);

	const getRecordsToExport = useCallback(async () => {
		if (props.getRecordsToExport) return props.getRecordsToExport();
		if (!exportFunc) return [];

		try {
			const { items, totalItems } = await exportFunc({ ...filters, export: true });
			if (items.length < totalItems) throw new Error("The server returned one page instead of every row");
			return items;
		} catch {
			PushErrorNotification(`Failed to export ${props.title ?? "records"}`);
			return [];
		}
	}, [exportFunc, filters, props]);

	const getData = useCallback(
		(currentPage: number, gridFilters: any) => {
			setFilters({ ...filters, ...gridFilters, currentPage });
		},
		[filters, setFilters],
	);

	const pageChanged = useCallback(
		(currentPage: number) => {
			setFilters({ ...filters, currentPage });
		},
		[filters, setFilters],
	);

	const setItemsPerPage = useCallback(
		(pageSize: number) => {
			setFilters({ ...filters, itemsPerPage: pageSize });
		},
		[filters, setFilters],
	);

	return (
		<Grid<T>
			{...rest}
			exportable={exportable}
			gridNameForExport={gridNameForExport}
			sortable={sortable}
			resizeable={resizeable}
			changeVisibility={changeVisibility}
			page={page}
			canSetItemsPerPage={canSetItemsPerPage}
			pageSizes={pageSizes}
			maxHeight={maxHeight}
			additionalActions={props.additionalActions}
			values={query.data?.items ?? []}
			totalItems={query.data?.totalItems ?? 0}
			currentPage={filters.currentPage}
			pageSize={filters.itemsPerPage}
			loading={query.isFetching || query.isLoading}
			isEnriching={isEnriching}
			refresh={props.refresh ?? query.refetch}
			pageChanged={pageChanged}
			setItemsPerPage={setItemsPerPage}
			getData={getData}
			reorderable={reorderable}
			loadGridState={gridApi ? loadGridState : props.loadGridState}
			saveGridState={gridApi ? saveGridState : props.saveGridState}
			stateful={props.stateful ?? !!gridApi}
			getRecordsToExport={getRecordsToExport}
		/>
	);
}
